/**
 * EmbeddedSubscriptionCheckout
 *
 * Wraps Stripe's <EmbeddedCheckoutProvider> + <EmbeddedCheckout> so any page
 * can drop in a fully-integrated checkout WITHOUT redirecting to Stripe's
 * domain.  Same Mux→CTA→checkout flow stays inside our page.
 *
 * Usage:
 *   <EmbeddedSubscriptionCheckout
 *     amountCents={3999}
 *     productName="Membresía VIP"
 *     country="US"             // → derives USD; "ES" → EUR
 *     returnUrl={`${origin}/exclusive/success?session_id={CHECKOUT_SESSION_ID}`}
 *     metadata={{ flow: "exclusive_intl_invite", ... }}
 *     onLoading={setLoading}
 *     onError={setError}
 *   />
 *
 * Currency is derived server-side from `country` (USD vs EUR) so the page
 * doesn't need to import the helper.
 */

import { Elements } from "@stripe/react-stripe-js";
import { EmbeddedCheckoutProvider, EmbeddedCheckout } from "@stripe/react-stripe-js";
import { loadStripe, type Stripe } from "@stripe/stripe-js";
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { cn } from "@/lib/utils";
import { mirrorFunnelEvent } from "@/lib/mirrorFunnelEvent";
import { PoweredByStripe } from "@/components/checkout/PoweredByStripe";
import {
  DatafastGoals,
  getDatafastAttributionPayload,
} from "@/lib/datafast";
import { getPostHogStripeMetadataPostHog } from "@/lib/posthogAnalytics";

/**
 * Device-adaptive height reservada por TODOS los estados (loading → loaded) para
 * que el drawer abra a un tamaño estable y no "salte" cuando carga el form de
 * Stripe. `min(64dvh,520px)` se adapta a pantallas chicas (iPhone SE) y grandes.
 */
const CHECKOUT_SHELL =
  "w-full px-1 py-2 min-h-[min(64dvh,520px)] sm:min-h-[480px]";

const STRIPE_PUBLISHABLE_KEY = (import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY as string) || "";

/** Singleton — Stripe.js should only be loaded once per app. */
let stripePromise: Promise<Stripe | null> | null = null;
function getStripe(): Promise<Stripe | null> {
  if (!stripePromise && STRIPE_PUBLISHABLE_KEY) {
    stripePromise = loadStripe(STRIPE_PUBLISHABLE_KEY);
  }
  return stripePromise ?? Promise.resolve(null);
}

interface EmbeddedSubscriptionCheckoutProps {
  /** Price in minor units (e.g. 3999 = $39.99 USD or €39.99 EUR). */
  amountCents: number;
  /** Product name shown by Stripe at checkout. */
  productName: string;
  /** ISO-2 country code. Drives currency selection (USD vs EUR). */
  country: string;
  /** Optional buyer email — Stripe asks for it if not provided. */
  buyerEmail?: string;
  /**
   * Where Stripe redirects after the embedded form completes. Must be
   * absolute. Use `{CHECKOUT_SESSION_ID}` as a placeholder for the session id.
   */
  returnUrl: string;
  /** Free-form metadata persisted on the Stripe Checkout Session + subscription. */
  metadata?: Record<string, string | undefined>;
  /** 1 = monthly (default), 3 = quarterly. */
  intervalCount?: number;
  /** Discount % (0-100) — Stripe will apply the matching PROMO<N> coupon. */
  discountPercentage?: number;
  /** Original price for strikethrough (in cents). */
  originalAmountCents?: number;
  /** Admin-generated invite code — sent to the API for server-side expiry enforcement. */
  inviteCode?: string;
  /**
   * @deprecated Stripe Price ID inline. La fuente de verdad para el Price ID
   * es `membership_products.stripe_price_id` (admin lo configura en
   * /admin/suscripciones). El endpoint hace el lookup automático por
   * `plan_tier`; este prop queda solo por compatibilidad y se ignora si no
   * se pasa explícitamente.
   */
  priceId?: string;
  onLoading?: (loading: boolean) => void;
  onError?: (error: string | null) => void;
  /** Optional callback fired when checkout completes successfully. */
  onComplete?: () => void;
  /** Fired once when Stripe embedded session (client_secret) is ready. */
  onSessionCreated?: () => void;
  /**
   * Cuando es true, el cobro usa exactamente `amountCents` (find-or-create por
   * lookup_key) e IGNORA cualquier `stripe_price_id` pre-cargado. Úsalo cuando el
   * precio lo define el admin en USD (`vip_price_usd`) y debe ser la fuente de verdad.
   * Preferí `priceId` + `promotionCodeId` para el flujo internacional DualCTA.
   */
  enforceAmount?: boolean;
  /**
   * Stripe Price ID explícito (`price_…`) — DualCTA / country-templates.
   * Tiene prioridad sobre membership_products lookup.
   */
  stripePriceId?: string;
  /**
   * Stripe Promotion Code ID (`promo_…`) a auto-aplicar. Tiene prioridad
   * sobre el promo default del fallback.
   */
  promotionCodeId?: string;
  /**
   * true cuando el Price es el fallback canónico (sin override de país).
   * El backend aplica FIRSTANA solo en este caso si no hay promotionCodeId.
   */
  useDefaultPromotion?: boolean;
}

export default function EmbeddedSubscriptionCheckout(props: EmbeddedSubscriptionCheckoutProps) {
  const {
    amountCents,
    productName,
    country,
    buyerEmail,
    returnUrl,
    metadata,
    intervalCount = 1,
    discountPercentage,
    originalAmountCents,
    inviteCode,
    priceId,
    onLoading,
    onError,
    onComplete,
    onSessionCreated,
    enforceAmount,
    stripePriceId,
    promotionCodeId,
    useDefaultPromotion,
  } = props;

  /** Prefer explicit DualCTA price; fall back to legacy `priceId` prop. */
  const resolvedPriceId = (stripePriceId || priceId || "").trim() || undefined;

  const { t } = useTranslation();

  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [currency, setCurrency] = useState<string>("usd");
  const sessionCreatedTrackedRef = useRef(false);

  useEffect(() => {
    let cancelled = false;
    onLoading?.(true);
    setError(null);
    sessionCreatedTrackedRef.current = false;

    const fetchSession = async () => {
      try {
        // Prefer explicit Stripe Price ID (country admin / catalog). Discount on a
        // Price is via promotionCodeId / useDefaultPromotion — NOT PROMO{n} coupons.
        // PROMO{n} is only for amount-based legacy path when there is no price_ id
        // (email-gate). Previously `!discountPercentage` blocked Price+promo when
        // UI passed discountPct for display/analytics → wrong charge + missing promo.
        const canUsePriceId = Boolean(resolvedPriceId) && intervalCount === 1;
        const discPct =
          typeof discountPercentage === "number" && discountPercentage > 0
            ? Math.round(discountPercentage)
            : metadata?.discount_percent_first_month
              ? Number(metadata.discount_percent_first_month)
              : 0;
        const originalCents =
          typeof originalAmountCents === "number" && originalAmountCents > 0
            ? originalAmountCents
            : amountCents;
        const hasDiscount =
          discPct > 0 ||
          Boolean(promotionCodeId?.trim()) ||
          useDefaultPromotion === true;
        const discountSource = promotionCodeId?.trim()
          ? "stripe_promotion_code"
          : useDefaultPromotion
            ? "default_firstana"
            : discPct > 0
              ? "promo_percent_coupon"
              : "none";

        DatafastGoals.initiateCheckout({
          plan: "vip",
          flow: "embedded_subscription",
          amount_cents: amountCents,
          currency: "usd",
        });
        const res = await fetch("/api/create-subscription-checkout", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            ...(canUsePriceId ? { priceId: resolvedPriceId } : {}),
            amount: amountCents,
            // La marca cobra en USD vía Stripe (incluso AR). Lo mandamos explícito
            // para que el monto enviado no se interprete en otra moneda.
            currency: "usd",
            productName,
            buyerEmail: buyerEmail ?? undefined,
            country,
            successUrl: returnUrl,
            cancelUrl: returnUrl, // ignored in embedded mode but the API expects it for hosted
            uiMode: "embedded",
            metadata: {
              ...metadata,
              ui_mode: "embedded",
              country: country || metadata?.country || "",
              ...(discPct > 0
                ? { discount_percent_first_month: String(discPct) }
                : {}),
              ...(hasDiscount ? { has_discount: "1" } : {}),
              // PostHog join: identity without email (blocked browsers still get session id)
              ...getPostHogStripeMetadataPostHog(),
            },
            ...getDatafastAttributionPayload(),
            intervalCount,
            // Cupón REAL por id (PROMO50): only when NO chargeable Price ID.
            // Server ensurePromoPercentCouponOnce + no stack con promotionCodeId.
            ...(!canUsePriceId && discPct > 0
              ? {
                  discountPercentage: discPct,
                  // hasDiscount requiere originalAmount > amount (lista > cobro 1er mes)
                  originalAmount:
                    originalCents > amountCents
                      ? originalCents
                      : Math.round(
                          amountCents /
                            (1 - Math.min(99, Math.max(1, discPct)) / 100),
                        ),
                  couponId:
                    (typeof metadata?.coupon_id === "string" &&
                    /^PROMO\d+/i.test(metadata.coupon_id)
                      ? metadata.coupon_id.trim().toUpperCase()
                      : null) || `PROMO${discPct}`,
                  ...(enforceAmount ? { enforceAmount: true } : {}),
                }
              : !canUsePriceId
                ? {
                    ...(enforceAmount ? { enforceAmount: true } : {}),
                  }
                : {}),
            ...(canUsePriceId && promotionCodeId
              ? { promotionCodeId: promotionCodeId.trim() }
              : {}),
            // FIRSTANA only when true fallback path (no country Price override).
            ...(useDefaultPromotion && !promotionCodeId
              ? { useDefaultPromotion: true }
              : {}),
            ...(inviteCode ? { invite_code: inviteCode } : {}),
          }),
        });
        const data = (await res.json()) as {
          client_secret?: string | null;
          currency?: string;
          error?: string;
          code?: string;
        };
        if (cancelled) return;
        if (!res.ok || !data.client_secret) {
          throw new Error(data.error || t("checkout.sessionError"));
        }
        setClientSecret(data.client_secret);
        if (data.currency) setCurrency(data.currency);
        if (!sessionCreatedTrackedRef.current) {
          sessionCreatedTrackedRef.current = true;
          const parentTemplate =
            typeof metadata?.parent_template === "string"
              ? metadata.parent_template
              : typeof metadata?.template === "string"
                ? metadata.template
                : "EmbeddedSubscriptionCheckout";
          const flow =
            typeof metadata?.flow === "string" ? metadata.flow : "embedded_checkout";
          const couponId =
            !canUsePriceId && discPct > 0
              ? (typeof metadata?.email_gate_promo === "string" &&
                metadata.email_gate_promo) ||
                `PROMO${discPct}`
              : undefined;
          mirrorFunnelEvent({
            event_name: "vip_membership_checkout_created",
            template: parentTemplate,
            template_variant:
              discPct === 50 || metadata?.email_gate_promo === "PROMO50"
                ? "stripe_embedded_promo50"
                : hasDiscount
                  ? "stripe_embedded_with_discount"
                  : "stripe_embedded",
            flow_name: flow,
            country: country || undefined,
            landing_path:
              typeof window !== "undefined" ? window.location.pathname : undefined,
            customer_email: buyerEmail,
            props: {
              amount_cents: amountCents,
              original_amount_cents: originalCents,
              list_amount_cents: originalCents,
              charge_amount_cents: amountCents,
              discount_percentage: discPct > 0 ? discPct : undefined,
              discount_percent_first_month: discPct > 0 ? discPct : undefined,
              has_discount: hasDiscount,
              discount_source: discountSource,
              coupon_id: couponId,
              promotion_code_id: promotionCodeId?.trim() || undefined,
              stripe_price_id: resolvedPriceId,
              use_default_promotion: useDefaultPromotion === true,
              coupon_duration: discPct > 0 ? "once" : undefined,
              first_month_only: Boolean(discPct > 0 || hasDiscount),
              has_buyer_email: Boolean(buyerEmail),
              email_gate: Boolean(metadata?.email_gate_promo || buyerEmail),
              funnel_name: "membership_email_gate_to_sub",
              funnel_stage: "checkout_ready",
              currency: (data.currency || "usd").toLowerCase(),
              gateway: "stripe",
              checkout_mode: "embedded",
              plan_tier: metadata?.plan_tier || "vip",
              product_name: productName,
              parent_template: parentTemplate,
              source: metadata?.source,
              flow_trace_id: metadata?.flow_trace_id,
              price_source: metadata?.price_source,
              revenue_potential_usd: amountCents > 0 ? amountCents / 100 : undefined,
            },
          });
          onSessionCreated?.();
        }
      } catch (err) {
        if (cancelled) return;
        const msg = err instanceof Error ? err.message : String(err);
        const parentTemplate =
          typeof metadata?.parent_template === "string"
            ? metadata.parent_template
            : typeof metadata?.template === "string"
              ? metadata.template
              : "EmbeddedSubscriptionCheckout";
        mirrorFunnelEvent({
          event_name: "vip_membership_checkout_error",
          template: parentTemplate,
          template_variant: "stripe_embedded",
          flow_name:
            typeof metadata?.flow === "string" ? metadata.flow : "embedded_checkout",
          country: country || undefined,
          landing_path:
            typeof window !== "undefined" ? window.location.pathname : undefined,
          customer_email: buyerEmail,
          props: {
            amount_cents: amountCents,
            discount_percentage: discountPercentage,
            original_amount_cents: originalAmountCents,
            has_buyer_email: Boolean(buyerEmail),
            error_message: msg.slice(0, 200),
            funnel_stage: "checkout_error",
            funnel_name: "membership_email_gate_to_sub",
            gateway: "stripe",
            checkout_mode: "embedded",
            plan_tier: metadata?.plan_tier || "vip",
            parent_template: parentTemplate,
          },
        });
        setError(msg);
        onError?.(msg);
      } finally {
        if (!cancelled) onLoading?.(false);
      }
    };

    void fetchSession();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    amountCents,
    productName,
    country,
    buyerEmail,
    returnUrl,
    intervalCount,
    discountPercentage,
    originalAmountCents,
    inviteCode,
    resolvedPriceId,
    promotionCodeId,
    useDefaultPromotion,
    enforceAmount,
  ]);

  const stripe = useMemo(() => getStripe(), []);

  if (!STRIPE_PUBLISHABLE_KEY) {
    return (
      <div className={cn(CHECKOUT_SHELL, "flex items-center justify-center")}>
        <div className="rounded-md border border-red-500/30 bg-red-500/5 p-4 text-sm text-red-300">
          {t("checkout.configMissing")}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={cn(CHECKOUT_SHELL, "flex items-center justify-center")}>
        <div className="rounded-md border border-red-500/30 bg-red-500/5 p-4 text-sm text-red-300">
          {error}
        </div>
      </div>
    );
  }

  if (!clientSecret) {
    // Mismo alto que el checkout cargado → el drawer no salta al pasar de
    // "preparando" a "form de pago". Spinner + label centrados.
    return (
      <div className={cn(CHECKOUT_SHELL, "flex flex-col items-center justify-center gap-3")}>
        <div className="relative h-8 w-8" aria-hidden>
          <div className="absolute inset-0 rounded-full border-2 border-white/15" />
          <div className="absolute inset-0 animate-spin rounded-full border-2 border-transparent border-t-white/80" />
        </div>
        <p className="text-xs font-medium tracking-wide text-white/55">
          {t("checkout.preparing")}
        </p>
        <PoweredByStripe variant="white" className="h-4 opacity-70" />
      </div>
    );
  }

  const handleComplete = () => {
    // Stripe redirige a return_url (Clerk profile) justo después.
    // Trackeamos el pago client-side antes de que el iframe desmonte.
    const parentTemplate =
      typeof metadata?.parent_template === "string"
        ? metadata.parent_template
        : typeof metadata?.template === "string"
          ? metadata.template
          : "EmbeddedSubscriptionCheckout";
    const discPct =
      typeof discountPercentage === "number" && discountPercentage > 0
        ? discountPercentage
        : undefined;
    mirrorFunnelEvent({
      event_name: "vip_membership_payment_submitted",
      template: parentTemplate,
      template_variant:
        discPct === 50 || metadata?.email_gate_promo === "PROMO50"
          ? "stripe_embedded_promo50"
          : "stripe_embedded",
      flow_name:
        typeof metadata?.flow === "string" ? metadata.flow : "embedded_checkout",
      country: country || undefined,
      landing_path:
        typeof window !== "undefined" ? window.location.pathname : undefined,
      customer_email: buyerEmail,
      props: {
        funnel_name: "membership_email_gate_to_sub",
        funnel_stage: "payment_submitted",
        amount_cents: amountCents,
        original_amount_cents: originalAmountCents,
        discount_percentage: discPct,
        coupon_id:
          discPct && discPct > 0
            ? metadata?.email_gate_promo || `PROMO${Math.round(discPct)}`
            : undefined,
        first_month_only: Boolean(discPct && discPct > 0),
        has_buyer_email: Boolean(buyerEmail),
        gateway: "stripe",
        checkout_mode: "embedded",
        plan_tier: metadata?.plan_tier || "vip",
        next_step: "clerk_profile",
        return_url_host:
          typeof window !== "undefined"
            ? (() => {
                try {
                  return new URL(returnUrl).host;
                } catch {
                  return undefined;
                }
              })()
            : undefined,
      },
    });
    onComplete?.();
  };

  return (
    <div data-currency={currency} className={CHECKOUT_SHELL}>
      <EmbeddedCheckoutProvider
        stripe={stripe}
        options={{
          clientSecret,
          onComplete: handleComplete,
        }}
      >
        <EmbeddedCheckout />
      </EmbeddedCheckoutProvider>
    </div>
  );
}

// Re-export Elements so consumers can wrap their tree if they need other
// Elements components alongside the embedded checkout. Embedded checkout
// alone does NOT require <Elements>.
export { Elements };
